import { useAdminUsers } from "@/hooks/use-admin-users";

function formatRelative(dateStr: string | null): string {
  if (!dateStr) return "never";
  const diff = Date.now() - new Date(dateStr).getTime();
  const days = Math.floor(diff / 86_400_000);
  if (days === 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.floor(months / 12)}y ago`;
}

function getInitial(email: string): string {
  return email.charAt(0).toUpperCase();
}

export default function UsersPage() {
  const { data, isLoading, error } = useAdminUsers();
  const users = data?.users ?? [];

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold">Users</h1>
        <p className="text-sm text-muted-foreground">All accounts that have requested OTPs</p>
      </div>

      {isLoading && (
        <div className="flex flex-wrap gap-3">
          {[...Array(4)].map((_, i) => (
            <div
              key={i}
              className="h-9 w-48 rounded-full bg-muted animate-pulse"
            />
          ))}
        </div>
      )}

      {error && (
        <p className="text-sm text-destructive">Failed to load users. Please try again.</p>
      )}

      {!isLoading && !error && users.length === 0 && (
        <p className="text-sm text-muted-foreground">No users found.</p>
      )}

      {!isLoading && !error && users.length > 0 && (
        <>
          <div className="flex flex-wrap gap-2 mb-8">
            {users.map((user) => (
              <div
                key={user.email}
                title={`Last login: ${formatRelative(user.lastAuthenticated)} · ${user.otpRequestCount} OTP requests`}
                className="flex items-center gap-2 rounded-full border bg-card px-3 py-1.5 text-sm shadow-sm hover:bg-muted/50 transition-colors"
              >
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300 text-xs font-bold">
                  {getInitial(user.email)}
                </span>
                <span className="font-medium">{user.email}</span>
                <span className="text-muted-foreground text-xs">
                  {formatRelative(user.lastAuthenticated)}
                </span>
              </div>
            ))}
          </div>

          <div className="rounded-xl border bg-card overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-muted-foreground">
                  <th className="px-4 py-3 font-medium">Email</th>
                  <th className="px-4 py-3 font-medium">Domain</th>
                  <th className="px-4 py-3 font-medium">Last Login</th>
                  <th className="px-4 py-3 font-medium">Last Session</th>
                  <th className="px-4 py-3 font-medium text-right">OTP Requests</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr
                    key={user.email}
                    className="border-b last:border-0 hover:bg-muted/40 transition-colors"
                  >
                    <td className="px-4 py-3 font-medium flex items-center gap-2">
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300 text-xs font-bold">
                        {getInitial(user.email)}
                      </span>
                      {user.email}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{user.domain}</td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {formatRelative(user.lastAuthenticated)}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {formatRelative(user.lastSession)}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">{user.otpRequestCount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
